// Church Health Assessment instrument — shared types, validation, and
// normalisation used by both the worker (publish route, seed) and the web
// (instrument editor). The instrument is versioned JSON in D1
// `health_instrument`; versions are immutable once published and each cycle
// pins the version it started with.
//
// Item ids are opaque strings, stable for the life of an item — analysis,
// the AI cache, and the critical/governance settings key on them. Display
// numbering (`section.no`) is presentation only and is rewritten from array
// position by `normalizeInstrument`. Item text may carry the inline markup
// subset from ./markup (**bold** *italic* _underline_).

/** Safeguarding = "critical": surfaced individually AND alarm-flagged when
 *  low. Governance: surfaced individually, never alarm-flagged. Wherever an
 *  item ends up in both sets, safeguarding wins. */
export type InstrumentDesignation = 'safeguarding' | 'governance';

export interface InstrumentItem {
  id: string;
  text: string;
  group?: string;
  note?: string;
  /** Follow-up questions: id of the parent scorecard item or reflection
   *  question in the SAME section. Only reflection items may carry this
   *  (a follow-up is always a written answer); one level deep only. */
  parentId?: string;
  /** Scorecard items only. Overrides the section's designation for this one
   *  item; 'none' explicitly opts an item out of a designated section. */
  designation?: InstrumentDesignation | 'none';
}

export interface InstrumentSection {
  no: number;
  id: string;
  title: string;
  aim: string;
  /** Blanket designation for every scorecard item in the section (items may
   *  override). Section-level is the preferred granularity (Steve,
   *  2026-07-06); item designations exist for the overlap cases. */
  designation?: InstrumentDesignation;
  scorecard: InstrumentItem[];
  reflection: InstrumentItem[];
}

export interface InstrumentDefinition {
  version: string;
  title: string;
  scale: { min: number; max: number; labels: Record<string, string> };
  sections: InstrumentSection[];
}

/** Guard against runaway definitions (the seed is ~25 KB). */
export const INSTRUMENT_MAX_JSON_BYTES = 400_000;

// This package compiles against lib ES2022 only; both consumers (Worker
// runtime, browsers) provide the WebCrypto global.
declare const crypto: { getRandomValues(array: Uint8Array): Uint8Array };

const ID_ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyz';

/** New editor-created ids: c_xxxxxx (scorecard), q_xxxxxx (reflection),
 *  sec_xxxxxx (section). The underscore prefix can never collide with the
 *  seed's s1c1/s1q2-style ids or section slugs. */
export function newInstrumentId(kind: 'c' | 'q' | 'sec'): string {
  const bytes = new Uint8Array(6);
  crypto.getRandomValues(bytes);
  let suffix = '';
  for (const b of bytes) suffix += ID_ALPHABET[b % 36];
  return `${kind}_${suffix}`;
}

const scrub = (s: string): string => s.replace(/\s*—\s*/g, ', ').trim();

/** Rewrite display numbers from array position, trim every string, and apply
 *  the module's no-em-dash rule. Pure — returns a new object. */
export function normalizeInstrument(def: InstrumentDefinition): InstrumentDefinition {
  const item = (it: InstrumentItem, allowDesignation: boolean): InstrumentItem => {
    const out: InstrumentItem = { id: it.id.trim(), text: scrub(it.text ?? '') };
    if (it.group?.trim()) out.group = scrub(it.group);
    if (it.note?.trim()) out.note = scrub(it.note);
    if (it.parentId?.trim()) out.parentId = it.parentId.trim();
    // Designations belong to scorecard items only; anything else (and any
    // unknown value) is dropped rather than carried into the immutable version.
    if (
      allowDesignation &&
      (it.designation === 'safeguarding' ||
        it.designation === 'governance' ||
        it.designation === 'none')
    ) {
      out.designation = it.designation;
    }
    return out;
  };
  const labels: Record<string, string> = {};
  for (const [k, v] of Object.entries(def.scale?.labels ?? {})) labels[k] = scrub(String(v));
  return {
    version: (def.version ?? '').trim(),
    title: scrub(def.title ?? ''),
    scale: { min: def.scale?.min, max: def.scale?.max, labels },
    sections: (def.sections ?? []).map((s, i) => ({
      no: i + 1,
      id: (s.id ?? '').trim(),
      title: scrub(s.title ?? ''),
      aim: scrub(s.aim ?? ''),
      ...(s.designation === 'safeguarding' || s.designation === 'governance'
        ? { designation: s.designation }
        : {}),
      scorecard: (s.scorecard ?? []).map((it) => item(it, true)),
      reflection: (s.reflection ?? []).map((it) => item(it, false)),
    })),
  };
}

/** Structural validation for publish. Returns human-readable errors; empty
 *  array = valid. Run on a normalised definition. */
export function validateInstrument(def: InstrumentDefinition): { errors: string[] } {
  const errors: string[] = [];
  if (!def.version) errors.push('Version is required.');
  if (!def.title) errors.push('Instrument title is required.');

  // The 1-4 scale is load-bearing: the D1 CHECK constraint, the analysis
  // band thresholds, and the survey scale UI all assume it.
  if (def.scale?.min !== 1 || def.scale?.max !== 4) {
    errors.push('The scale must run 1 to 4.');
  }
  for (const k of ['1', '2', '3', '4']) {
    if (!def.scale?.labels?.[k]?.trim()) errors.push(`Scale label ${k} is required.`);
  }

  if (!Array.isArray(def.sections) || def.sections.length === 0) {
    errors.push('At least one section is required.');
    return { errors };
  }

  const allIds = new Set<string>();
  // Ids surface as raw column headers in scores.csv and as D1/cache keys, so
  // restrict them to a safe charset (the seed ids and editor-minted ids all
  // fit) — this blocks a hand-crafted id carrying a CSV formula trigger or
  // delimiter into a facilitator's spreadsheet.
  const ID_RE = /^[A-Za-z0-9_]+$/;
  const claim = (id: string, what: string): void => {
    if (!ID_RE.test(id)) {
      errors.push(`Id "${id}" (${what}) may only contain letters, numbers, and underscores.`);
    }
    if (allIds.has(id)) errors.push(`Duplicate id "${id}" (${what}).`);
    allIds.add(id);
  };

  def.sections.forEach((s, i) => {
    const label = s.title ? `"${s.title}"` : `#${i + 1}`;
    if (!s.id) errors.push(`Section ${label} has no id.`);
    else claim(s.id, `section ${label}`);
    if (!s.title) errors.push(`Section #${i + 1} needs a title.`);
    if (typeof s.aim !== 'string') errors.push(`Section ${label} aim must be text.`);
    if (!Array.isArray(s.scorecard) || !Array.isArray(s.reflection)) {
      errors.push(`Section ${label} is missing its question lists.`);
      return;
    }

    if (
      s.designation !== undefined &&
      s.designation !== 'safeguarding' &&
      s.designation !== 'governance'
    ) {
      errors.push(`Section ${label} has an unknown designation "${String(s.designation)}".`);
    }

    const sectionItemIds = new Set<string>();
    for (const it of [...s.scorecard, ...s.reflection]) {
      if (!it.id) errors.push(`A question in section ${label} has no id.`);
      else {
        claim(it.id, `question in section ${label}`);
        sectionItemIds.add(it.id);
      }
      if (!it.text?.trim())
        errors.push(`A question in section ${label} has no text (id "${it.id}").`);
    }
    for (const it of s.scorecard) {
      if (
        it.designation !== undefined &&
        it.designation !== 'safeguarding' &&
        it.designation !== 'governance' &&
        it.designation !== 'none'
      ) {
        errors.push(
          `Scale item "${it.id}" has an unknown designation "${String(it.designation)}".`,
        );
      }
    }
    for (const it of s.reflection) {
      if (it.designation !== undefined) {
        errors.push(
          `Written question "${it.id}" cannot carry a designation (designations apply to scale items).`,
        );
      }
    }

    // Follow-up rules: reflection-only, parent in the same section, no chains.
    for (const it of s.scorecard) {
      if (it.parentId)
        errors.push(
          `Scale item "${it.id}" cannot be a follow-up (follow-ups are written questions).`,
        );
    }
    const parents = new Map(s.reflection.map((q) => [q.id, q]));
    for (const q of s.reflection) {
      if (!q.parentId) continue;
      if (!sectionItemIds.has(q.parentId)) {
        errors.push(
          `Follow-up "${q.id}" points at "${q.parentId}", which is not in the same section.`,
        );
      }
      const parent = parents.get(q.parentId);
      if (parent?.parentId) {
        errors.push(
          `Follow-up "${q.id}" is attached to another follow-up ("${q.parentId}") — one level only.`,
        );
      }
    }
  });

  if (JSON.stringify(def).length > INSTRUMENT_MAX_JSON_BYTES) {
    errors.push('The instrument is too large.');
  }
  return { errors };
}

/** True when the definition carries its own safeguarding/governance markings
 *  (any section designation, or any item designation including an explicit
 *  'none'). Designated versions ignore the module-settings fallback lists. */
export function hasDesignations(def: InstrumentDefinition): boolean {
  for (const s of def.sections) {
    if (s.designation) return true;
    for (const it of s.scorecard) if (it.designation) return true;
  }
  return false;
}

/** Expand module-settings designation tokens against a definition. A token
 *  matches a whole section (its slug id like "governance", or its display
 *  number like "s12") or a single scorecard item id (like "s12c3"). Section
 *  tokens expand to every scorecard item in that section. */
export function expandDesignationTokens(
  tokens: string[],
  def: InstrumentDefinition,
): { ids: string[]; unmatched: string[] } {
  const itemIds = new Set<string>();
  const bySectionToken = new Map<string, string[]>();
  for (const s of def.sections) {
    const ids = s.scorecard.map((it) => it.id);
    bySectionToken.set(s.id.toLowerCase(), ids);
    bySectionToken.set(`s${s.no}`, ids);
    for (const id of ids) itemIds.add(id);
  }
  const ids: string[] = [];
  const seen = new Set<string>();
  const unmatched: string[] = [];
  for (const raw of tokens) {
    const token = raw.trim();
    if (!token) continue;
    const section = bySectionToken.get(token.toLowerCase());
    const expandedIds = section ?? (itemIds.has(token) ? [token] : null);
    if (!expandedIds) {
      unmatched.push(token);
      continue;
    }
    for (const id of expandedIds) {
      if (!seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    }
  }
  return { ids, unmatched };
}

/** The safeguarding (critical) and governance item-id sets a cycle's analysis
 *  should use. A version with its own designations is the source of truth
 *  (item override beats section blanket; 'none' opts out); otherwise the
 *  module-settings token lists apply, expanded against this definition.
 *  Either way, safeguarding wins where an item lands in both sets. */
export function effectiveDesignations(
  def: InstrumentDefinition,
  settingsFallback: { critical: string[]; governance: string[] },
): { criticalIds: string[]; governanceIds: string[]; source: 'instrument' | 'settings' } {
  let criticalIds: string[];
  let governanceIds: string[];
  let source: 'instrument' | 'settings';
  if (hasDesignations(def)) {
    source = 'instrument';
    criticalIds = [];
    governanceIds = [];
    for (const s of def.sections) {
      for (const it of s.scorecard) {
        const d = it.designation ?? s.designation;
        if (d === 'safeguarding') criticalIds.push(it.id);
        else if (d === 'governance') governanceIds.push(it.id);
      }
    }
  } else {
    source = 'settings';
    criticalIds = expandDesignationTokens(settingsFallback.critical, def).ids;
    governanceIds = expandDesignationTokens(settingsFallback.governance, def).ids;
  }
  const critical = new Set(criticalIds);
  return { criticalIds, governanceIds: governanceIds.filter((id) => !critical.has(id)), source };
}

/** Advisory (never blocking) publish warnings. For a self-designated version,
 *  the only honest warning is a missing safeguarding set; for older-style
 *  versions, unmatched settings tokens mean flagging silently stops. */
export function instrumentWarnings(
  def: InstrumentDefinition,
  criticalItems: string[],
  governanceItems: string[],
): string[] {
  const warnings: string[] = [];
  const effective = effectiveDesignations(def, {
    critical: criticalItems,
    governance: governanceItems,
  });
  if (effective.source === 'settings') {
    for (const token of expandDesignationTokens(criticalItems, def).unmatched) {
      warnings.push(
        `Safeguarding (critical) entry "${token}" matches nothing in this version — it will not be flagged.`,
      );
    }
    for (const token of expandDesignationTokens(governanceItems, def).unmatched) {
      warnings.push(
        `Governance entry "${token}" matches nothing in this version — it will not be flagged.`,
      );
    }
  }
  if (effective.criticalIds.length === 0) {
    warnings.push('No safeguarding questions are designated in this version.');
  }
  return warnings;
}

// ── In-place edit classification ──────────────────────────────────────────────
// A published version may be edited in place, with friction graduated by how
// much changed (Steve, 2026-07-07). The classifier is the single source of truth
// for that friction, shared by the worker route (authoritative) and the editor
// (UI hint). Both run on normalised definitions; the helpers normalise again so
// a caller can't accidentally compare un-normalised input.

export type InstrumentChangeKind = 'none' | 'wording' | 'additive' | 'destructive';

export interface InstrumentChange {
  kind: InstrumentChangeKind;
  /** Specific structural blockers, populated for 'destructive'. */
  reasons: string[];
}

const arraysEqual = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((v, i) => v === b[i]);

/** Every element of `sub` appears in `arr` in the same relative order (arr may
 *  interleave extra elements). Missing elements fail. */
function isSubsequence(sub: string[], arr: string[]): boolean {
  let i = 0;
  for (const v of arr) {
    if (i < sub.length && sub[i] === v) i++;
  }
  return i === sub.length;
}

/** Why an old→new id list is not a pure insertion: a removal, or a reorder. */
function orderBlocker(oldIds: string[], newIds: string[], noun: string): string | null {
  const present = new Set(newIds);
  if (oldIds.some((id) => !present.has(id))) return `A ${noun} was removed.`;
  if (!isSubsequence(oldIds, newIds)) {
    return `${noun.charAt(0).toUpperCase()}${noun.slice(1)} order changed.`;
  }
  return null;
}

/** Classify draft vs stored for the graduated in-place edit. `wording` = only
 *  display text differs; `additive` = existing items all kept in place (same
 *  order, designation, parent) with new items inserted; `destructive` = a
 *  removal, reorder, re-designation, re-parent, or cross-section move — the
 *  moves that can misalign answers already collected. */
export function classifyInstrumentChange(
  oldDef: InstrumentDefinition,
  newDef: InstrumentDefinition,
): InstrumentChange {
  const oldN = normalizeInstrument(oldDef);
  const newN = normalizeInstrument(newDef);
  if (JSON.stringify(oldN) === JSON.stringify(newN)) return { kind: 'none', reasons: [] };
  if (oldN.version !== newN.version) {
    return { kind: 'destructive', reasons: ['The version number changed.'] };
  }

  const oldSecIds = oldN.sections.map((s) => s.id);
  const newSecIds = newN.sections.map((s) => s.id);
  const newSecById = new Map(newN.sections.map((s) => [s.id, s]));

  // Wording: structure byte-identical (ids, order, designations, parents), so
  // only text can account for the difference detected above.
  const structuralIdentical =
    arraysEqual(oldSecIds, newSecIds) &&
    oldN.sections.every((s, i) => {
      const n = newN.sections[i];
      if (!n) return false;
      return (
        s.designation === n.designation &&
        arraysEqual(
          s.scorecard.map((it) => it.id),
          n.scorecard.map((it) => it.id),
        ) &&
        arraysEqual(
          s.reflection.map((it) => it.id),
          n.reflection.map((it) => it.id),
        ) &&
        s.scorecard.every((it, j) => it.designation === n.scorecard[j]?.designation) &&
        s.reflection.every((it, j) => it.parentId === n.reflection[j]?.parentId)
      );
    });
  if (structuralIdentical) return { kind: 'wording', reasons: [] };

  // Additive: every old id survives in its own section, in order, unchanged in
  // designation/parent; only new ids were inserted.
  const reasons: string[] = [];
  const secBlock = orderBlocker(oldSecIds, newSecIds, 'section');
  if (secBlock) reasons.push(secBlock);

  for (const s of oldN.sections) {
    const n = newSecById.get(s.id);
    if (!n) continue; // removal already flagged by secBlock
    const label = s.title || s.id;
    if (s.designation !== n.designation) {
      reasons.push(`The designation of section "${label}" changed.`);
    }
    const oldScoreIds = s.scorecard.map((it) => it.id);
    const scoreBlock = orderBlocker(
      oldScoreIds,
      n.scorecard.map((it) => it.id),
      'question',
    );
    if (scoreBlock) reasons.push(`${scoreBlock} (section "${label}")`);
    else {
      const nById = new Map(n.scorecard.map((it) => [it.id, it]));
      for (const it of s.scorecard) {
        if (nById.get(it.id)?.designation !== it.designation) {
          reasons.push(`A question's designation changed in section "${label}".`);
          break;
        }
      }
    }
    const oldReflIds = s.reflection.map((it) => it.id);
    const reflBlock = orderBlocker(
      oldReflIds,
      n.reflection.map((it) => it.id),
      'written question',
    );
    if (reflBlock) reasons.push(`${reflBlock} (section "${label}")`);
    else {
      const nById = new Map(n.reflection.map((it) => [it.id, it]));
      for (const it of s.reflection) {
        if (nById.get(it.id)?.parentId !== it.parentId) {
          reasons.push(`A follow-up question was re-attached in section "${label}".`);
          break;
        }
      }
    }
  }

  if (reasons.length === 0) return { kind: 'additive', reasons: [] };
  return { kind: 'destructive', reasons };
}

export interface InstrumentTextChange {
  /** Stable machine path, e.g. "sec_x.title" or "c_y.text". */
  path: string;
  /** Human label, e.g. "Section 3, question 2". */
  label: string;
  before: string;
  after: string;
}

/** Every changed text field as a before→after pair. Only compares fields that
 *  exist in both (matched by id), so it is meaningful for wording and additive
 *  changes; a removed item never appears. */
export function diffInstrumentText(
  oldDef: InstrumentDefinition,
  newDef: InstrumentDefinition,
): InstrumentTextChange[] {
  const oldN = normalizeInstrument(oldDef);
  const newN = normalizeInstrument(newDef);
  const out: InstrumentTextChange[] = [];

  if (oldN.title !== newN.title) {
    out.push({ path: 'title', label: 'Instrument title', before: oldN.title, after: newN.title });
  }
  for (const k of ['1', '2', '3', '4']) {
    const before = oldN.scale.labels[k] ?? '';
    const after = newN.scale.labels[k] ?? '';
    if (before !== after)
      out.push({ path: `scale.${k}`, label: `Scale label ${k}`, before, after });
  }

  const newSecById = new Map(newN.sections.map((s) => [s.id, s]));
  for (const s of oldN.sections) {
    const n = newSecById.get(s.id);
    if (!n) continue;
    if (s.title !== n.title) {
      out.push({
        path: `${s.id}.title`,
        label: `Section ${s.no} title`,
        before: s.title,
        after: n.title,
      });
    }
    if (s.aim !== n.aim) {
      out.push({ path: `${s.id}.aim`, label: `Section ${s.no} aim`, before: s.aim, after: n.aim });
    }
    const nItemById = new Map([...n.scorecard, ...n.reflection].map((it) => [it.id, it]));
    const lists: Array<[InstrumentItem[], string]> = [
      [s.scorecard, 'question'],
      [s.reflection, 'written question'],
    ];
    for (const [items, noun] of lists) {
      items.forEach((it, idx) => {
        const m = nItemById.get(it.id);
        if (!m) return;
        const where = `Section ${s.no}, ${noun} ${idx + 1}`;
        if (it.text !== m.text) {
          out.push({ path: `${it.id}.text`, label: where, before: it.text, after: m.text });
        }
        if ((it.note ?? '') !== (m.note ?? '')) {
          out.push({
            path: `${it.id}.note`,
            label: `${where} note`,
            before: it.note ?? '',
            after: m.note ?? '',
          });
        }
        if ((it.group ?? '') !== (m.group ?? '')) {
          out.push({
            path: `${it.id}.group`,
            label: `${where} group`,
            before: it.group ?? '',
            after: m.group ?? '',
          });
        }
      });
    }
  }
  return out;
}
